<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <div class="rd-chips" role="group" :aria-label="$t('order_status')">
        <button
          v-for="chip in statusChips"
          :key="chip.value"
          type="button"
          class="rd-chip"
          :aria-pressed="String(orderStatus === chip.value)"
          @click="orderStatus = chip.value"
        >
          {{ chip.label }}
          <b class="rd-num">{{ chip.count }}</b>
        </button>
      </div>
    </report-tools>

    <report-state v-if="loading && !report" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-account-off-outline" :title="emptyText"></report-state>

    <div v-else class="rd-card rd-card--scroll">
      <table class="rd-tbl rd-tbl--workers">
        <thead>
          <tr>
            <th scope="col" class="rd-col-id" :aria-sort="ariaSort('id')">
              <button type="button" @click="sortBy('id')">ID {{ sortArrow("id") }}</button>
            </th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("worker") }} {{ sortArrow("name") }}</button>
            </th>
            <th v-if="hasBranches" scope="col" :aria-sort="ariaSort('companyName')">
              <button type="button" @click="sortBy('companyName')">
                {{ $t("branch") }} {{ sortArrow("companyName") }}
              </button>
            </th>
            <th v-if="hasExternalCode" scope="col" :aria-sort="ariaSort('externalCode')">
              <button type="button" @click="sortBy('externalCode')">
                {{ $t("external_code") }} {{ sortArrow("externalCode") }}
              </button>
            </th>
            <th
              v-for="shift in visibleShifts"
              :key="shift.id"
              scope="col"
              class="is-center"
              :aria-sort="ariaSort(shift.key)"
            >
              <button type="button" @click="sortBy(shift.key)">
                <report-shift-tag :shift="shift.id"></report-shift-tag>
                {{ sortArrow(shift.key) }}
              </button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('ordered')">
              <button type="button" @click="sortBy('ordered')">{{ $t("ordered") }} {{ sortArrow("ordered") }}</button>
            </th>
            <th scope="col" class="is-right rd-col-taken" :aria-sort="ariaSort('taken')">
              <button type="button" @click="sortBy('taken')">{{ $t("taken") }} {{ sortArrow("taken") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('difference')">
              <button type="button" @click="sortBy('difference')">
                {{ $t("difference") }} {{ sortArrow("difference") }}
              </button>
            </th>
            <th scope="col" :aria-sort="ariaSort('stateRank')">
              <button type="button" @click="sortBy('stateRank')">{{ $t("status") }} {{ sortArrow("stateRank") }}</button>
            </th>
            <th scope="col" class="rd-col-go"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.id"
            data-open
            tabindex="0"
            :class="{ 'is-selected': isOpen(row), 'is-idle': row.state === 'off' }"
            @click="openWorker(row, $event)"
            @keydown.enter.prevent="openWorker(row, $event)"
            @keydown.space.prevent="openWorker(row, $event)"
          >
            <td class="rd-col-id rd-num rd-muted">{{ row.id }}</td>
            <td class="is-name" :title="row.name">
              <span class="rd-person">
                <span class="rd-avatar rd-avatar--sm">{{ row.initials }}</span>
                <span class="rd-person__name">{{ row.name }}</span>
              </span>
            </td>
            <td v-if="hasBranches" class="rd-muted" :title="row.companyName">{{ row.companyName || "–" }}</td>
            <td v-if="hasExternalCode" class="rd-num rd-muted">{{ row.externalCode || "–" }}</td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-center">
              <span v-if="row[shift.key]" class="rs-tag rs-tag--count" :data-shift="shift.id">
                {{ row[shift.key] }}
                <span class="rs-tag__sr">{{ $t("report_mobile.shift_n", { n: shift.id }) }}</span>
              </span>
              <template v-else>
                <span class="rd-dash" aria-hidden="true">–</span>
                <span class="rd-visually-hidden">0</span>
              </template>
            </td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.ordered }">{{ row.ordered }}</td>
            <td class="is-right rd-num rd-col-taken" :class="{ 'is-zero': !row.taken }">{{ row.taken }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': row.difference === 0, 'is-alert': row.difference > 0 }">
              {{ row.difference }}
            </td>
            <td>
              <span class="rd-status" :class="`rd-status--${row.state}`">{{ row.statusLabel }}</span>
            </td>
            <td class="rd-col-go"><v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon></td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td class="rd-col-id"></td>
            <td>{{ $t("total") }}</td>
            <td v-if="hasBranches"></td>
            <td v-if="hasExternalCode"></td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-center rd-num">{{ totals[shift.key] }}</td>
            <td class="is-right rd-num">{{ totals.ordered }}</td>
            <td class="is-right rd-num rd-col-taken">{{ totals.taken }}</td>
            <td class="is-right rd-num">{{ totals.difference }}</td>
            <td></td>
            <td class="rd-col-go"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import CompanyReportTabEmployees from "@/components/company/reports/companyTabs/companyReportTabEmployees.vue";
import { SHIFTS } from "@/constants/shifts";
import hasWorkerList from "@/components/report/shared/hasWorkerList";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import ReportDesktopWorkerMonth from "@/components/report/desktop/screens/ReportDesktopWorkerMonth.vue";

// Šta je hitnije u sortiranju po statusu: nije preuzeo → deo → nije poručio → preuzeo.
const STATE_RANK = { none: 3, part: 2, off: 1, all: 0 };

/**
 * Radnici firme na računaru: spisak radnika izabranih firmi sa smenama u kojima su poručili, poručeno / preuzeto /
 * razlikom i statusom (Nije poručio, Nije preuzeo, deo, Preuzeo). Filter statusa su čipovi sa brojevima; klik na
 * red otvara mesečni pregled radnika u panelu. Firme i pretragu čita iz zajedničke trake. Nasleđuje učitavanje,
 * filter statusa, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopEmployees",
  extends: CompanyReportTabEmployees,
  mixins: [hasWorkerList, hasTableSort],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    selectedId: null,
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    /** Firma radnika za mesečni pregled: samo kad je izabrana jedna (inače se ne zna kojoj radnik pripada). */
    selectedCompany() {
      const companies = this.$store.state.report.selectedFoodCompanies || [];
      return companies.length === 1 ? companies[0] : null;
    },
    /** Čipovi statusa sa brojevima; vrednosti su isti statusi kao u starom izborniku (orderStatus). */
    statusChips() {
      const users = (this.report && this.report.users) || [];
      const ordered = users.filter((user) => (user.order_count || 0) > 0);
      const taken = ordered.filter((user) => (user.order_payed || 0) > 0).length;
      const counts = [users.length, ordered.length, users.length - ordered.length, taken, ordered.length - taken];
      return this.statusItems.map((item, index) => ({ value: item.value, label: item.text, count: counts[index] }));
    },
    /** Smene 1–3 uvek, četvrta samo kad neko u njoj ima narudžbu. */
    visibleShifts() {
      const users = this.filteredUsers;
      return SHIFTS.filter((shift) => shift.id < 4 || users.some((user) => Number(user[shift.key]) > 0));
    },
    hasExternalCode() {
      return this.filteredUsers.some((user) => user.external_code);
    },
    allRows() {
      return this.filteredUsers.map((user) => this.toRow(user));
    },
    rows() {
      const search = this.filters.search;
      const matched = search
        ? this.customSearch(this.allRows, [{ value: "name" }, { value: "idText" }, { value: "externalCode" }, { value: "companyName" }], search)
        : this.allRows;
      // abecedno kao druga mera: redovi sa istom vrednošću ostaju po imenu
      const byName = [...matched].sort((a, b) => a.name.localeCompare(b.name));
      return this.sortRows(byName);
    },
    totals() {
      const totals = { ordered: 0, taken: 0, difference: 0 };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = 0;
      });
      this.rows.forEach((row) => {
        totals.ordered += row.ordered;
        totals.taken += row.taken;
        totals.difference += row.difference;
        SHIFTS.forEach((shift) => {
          totals[shift.key] += row[shift.key];
        });
      });
      return totals;
    },
    kpis() {
      return [
        { key: "ordered", label: this.$t("ordered"), value: this.totals.ordered, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.totals.taken },
        { key: "difference", label: this.$t("difference"), value: this.totals.difference },
      ];
    },
    emptyText() {
      if (this.filters.search) {
        return this.$t("report_mobile.no_results_for", { search: this.filters.search });
      }
      return this.report && this.report.users && this.report.users.length
        ? this.$t("report_mobile.no_workers_in_view")
        : this.$t("no_employee_report_data");
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    /** Red tabele: radnik iz odgovora (users) + stanje narudžbe i preuzimanja. */
    toRow(user) {
      const ordered = user.order_count || 0;
      const taken = user.order_payed || 0;
      const state = ordered <= 0 ? "off" : taken <= 0 ? "none" : taken >= ordered ? "all" : "part";
      const name = [this.toLatin(user.name), this.toLatin(user.lastname)].filter(Boolean).join(" ").trim();
      const row = {
        id: user.id,
        idText: String(user.id),
        externalCode: user.external_code || "",
        companyName: this.toLatin(user.company_name || ""),
        name,
        initials: [user.name, user.lastname]
          .filter(Boolean)
          .map((part) => this.toLatin(part).charAt(0))
          .join(""),
        ordered,
        taken,
        difference: ordered - taken,
        state,
        stateRank: STATE_RANK[state],
        statusLabel: state === "off" ? this.$t("report_desktop.status_not_ordered") : this.statusLabel(state, taken, ordered),
        source: user,
      };
      SHIFTS.forEach((shift) => {
        row[shift.key] = Number(user[shift.key]) || 0;
      });
      return row;
    },
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `worker-${row.id}`);
    },
    /** Mesečni pregled radnika u panelu. */
    openWorker(worker, event) {
      this.selectedId = worker.id;
      const employee = this.toEmployeeContext(worker);
      this.$emit("open-panel", {
        opener: event && event.currentTarget,
        key: `worker-${worker.id}`,
        root: true,
        wide: true,
        title: worker.name,
        subtitle: [`ID ${worker.id}`, employee.companyName].filter(Boolean).join(" · "),
        component: ReportDesktopWorkerMonth,
        props: { worker: employee, compact: true },
      });
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("worker"),
        ...(this.hasBranches ? [this.$t("branch")] : []),
        ...(this.hasExternalCode ? [this.$t("external_code")] : []),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("ordered"),
        this.$t("taken"),
        this.$t("difference"),
        this.$t("status"),
      ];
      const rows = this.rows.map((row) => [
        row.id,
        row.name,
        ...(this.hasBranches ? [row.companyName] : []),
        ...(this.hasExternalCode ? [row.externalCode] : []),
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.ordered,
        row.taken,
        row.difference,
        row.statusLabel,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
